import { useState } from 'react';
import type { RunTestsResult, TestRunUiStatus } from '../types';
import { RunTestsTestRow } from './RunTestsTestRow';

type FileResult = RunTestsResult['fileResults'][number];

function statusOf(t: NonNullable<FileResult['tests']>[number]): TestRunUiStatus {
  return (t.status as TestRunUiStatus | undefined) ?? (t.passed ? 'passed' : 'failed');
}

export function RunTestsFileRow({ file }: { file: FileResult }) {
  const hasCrash = !!(file.transpileError || file.runtimeError);
  const tests = file.tests ?? [];
  const hasTests = tests.length > 0;
  const failingTests = tests.filter(t => {
    const s = statusOf(t);
    return s === 'failed' || s === 'error';
  });
  const runningTests = tests.filter(t => {
    const s = statusOf(t);
    return s === 'running' || s === 'pending';
  });
  const allPassed = !hasCrash && hasTests && failingTests.length === 0 && runningTests.length === 0;
  const anyFailed = hasCrash || failingTests.length > 0;
  const anyRunning = runningTests.length > 0;
  const hasDetails = hasCrash || hasTests;

  // Auto-expand the file row when there's something the user needs to see
  // mid-flight (running tests) or that explains a failure (crashes /
  // failing assertions). Passing-only files stay collapsed so the panel
  // doesn't drown out the signal with green rows.
  const [open, setOpen] = useState<boolean>(anyFailed || anyRunning);

  const icon = anyFailed
    ? '✗'
    : anyRunning
      ? '◐'
      : allPassed
        ? '✓'
        : '○';
  const iconClass = anyFailed
    ? 'test-status fail'
    : anyRunning
      ? 'test-status running'
      : allPassed
        ? 'test-status pass'
        : 'test-status pending';

  const passedCount = tests.filter(t => statusOf(t) === 'passed').length;

  return (
    <div className="pipeline-test-file">
      <div
        className={`pipeline-test-file-header${hasDetails ? ' clickable' : ''}`}
        onClick={hasDetails ? () => setOpen(!open) : undefined}
        style={hasDetails ? { cursor: 'pointer' } : undefined}
      >
        {hasDetails && (
          <span className="step-expand-icon">{open ? '▾' : '▸'}</span>
        )}
        <span
          className={iconClass}
          data-spinning={anyRunning && !anyFailed ? 'true' : undefined}
        >
          {icon}
        </span>
        <span className="pipeline-test-file-path">{file.path}</span>
        {hasTests && (
          <span className="pipeline-test-count">
            {passedCount}/{tests.length}
            {failingTests.length > 0 && (
              <span className="pipeline-test-count-fail">
                {' '}· {failingTests.length} failed
              </span>
            )}
          </span>
        )}
      </div>
      {open && hasCrash && (
        <div className="pipeline-test-crash">
          <div className="pipeline-test-crash-label">
            {file.transpileError ? 'Transpile error' : 'Runtime error'}
          </div>
          <pre className="pipeline-test-crash-text">
            {file.transpileError ?? file.runtimeError}
          </pre>
        </div>
      )}
      {open && hasTests && (
        <div className="pipeline-test-list">
          {tests.map((t, i) => (
            <RunTestsTestRow key={`${t.fullName ?? t.name}-${i}`} test={t} />
          ))}
        </div>
      )}
    </div>
  );
}
